import React from 'react';
import { useTranslation } from "react-i18next";
import PanelWrapper from "../PanelWrapper.js";
import AccessibleChartRegion from "./AccessibleChartRegion";

/**
 * Shared wrapper for chart panels. Handles data fetching, loading, error, and
 * empty-state rendering. Passes panelData to the children render function.
 * The render function must return the data sent to Carbon together with its UI,
 * so the accessible alternative describes the same chart state.
 *
 * This is a wrapper around PanelWrapper with showDescription=true by default.
 *
 * Usage:
 *   <ChartPanelWrapper chart={chart}>
 *     {(panelData, renderChart) => {
 *       const data = transform(panelData);
 *       return renderChart(data, <MyChart data={data} options={opts} />);
 *     }}
 *   </ChartPanelWrapper>
 */
const ChartPanelWrapper = ({ chart, children }) => {
    const { t } = useTranslation();

    return (
        <PanelWrapper panel={chart} showDescription={true}>
            {(panelData) => {
                const renderChart = (data, content) => (
                    <AccessibleChartRegion
                        title={chart.title || chart.name || t("charts.defaultTitle")}
                        chart={chart}
                        data={data}
                    >
                        {content}
                    </AccessibleChartRegion>
                );

                return children(panelData, renderChart);
            }}
        </PanelWrapper>
    );
};

export default ChartPanelWrapper;
